import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type { BrandingRequest, BrandingResponse } from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';

/**
 * How the clinic looks to its own patients (ASM-006).
 *
 * A logo, the two display names and one accent colour — and the screen says so, because the first
 * question a partner asks is "can we theme the whole app". The answer is no, and the reason is
 * legibility: every patient screen has a contrast floor to meet in two directions and two
 * languages, and a clinic that could restyle it is a clinic that could break it.
 *
 * The preview is not decoration. An accent chosen from a brand book on a designer's monitor can be
 * unreadable on a phone in daylight, and showing the actual button next to the picker is the
 * cheapest way to find that out before a patient does.
 */
const DEFAULT_ACCENT = '#B08D57';

interface BrandingDraft {
  readonly displayNameEn: string;
  readonly displayNameAr: string;
  readonly logoObjectKey: string;
  readonly accentColour: string;
}

export function BrandingPage() {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const canManage = useAuthStore((state) => state.permissions).has('CLINIC_SETTINGS_MANAGE');
  const [draft, setDraft] = useState<BrandingDraft | null>(null);

  const branding = useApiQuery<BrandingResponse>(
    queryKeys.branding(clinicId ?? ''),
    `/clinics/${clinicId}/branding`,
    { enabled: Boolean(clinicId) },
  );

  const save = useApiMutation<BrandingResponse, BrandingRequest>(
    (body) => ({ path: `/clinics/${clinicId}/branding`, options: { method: 'PUT', body } }),
    { invalidates: [queryKeys.branding(clinicId ?? '')], onSuccess: () => setDraft(null) },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.branding')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.branding')} description={t('branding.description')} />

      <AsyncSection query={branding}>
        {(saved) => {
          const current: BrandingDraft = draft ?? {
            displayNameEn: saved.displayNameEn ?? '',
            displayNameAr: saved.displayNameAr ?? '',
            logoObjectKey: saved.logoObjectKey ?? '',
            accentColour: saved.accentColour ?? DEFAULT_ACCENT,
          };
          const update = (change: Partial<BrandingDraft>) => setDraft({ ...current, ...change });

          return (
            <form
              className="panel"
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate({
                  displayNameEn: current.displayNameEn.trim() || undefined,
                  displayNameAr: current.displayNameAr.trim() || undefined,
                  logoObjectKey: current.logoObjectKey.trim() || undefined,
                  accentColour: current.accentColour || undefined,
                });
              }}
            >
              <div className="form-grid">
                <label className="field">
                  <span className="field__label">{t('branding.nameAr')}</span>
                  <input
                    className="field__input"
                    dir="rtl"
                    lang="ar"
                    maxLength={160}
                    value={current.displayNameAr}
                    disabled={!canManage}
                    onChange={(event) => update({ displayNameAr: event.target.value })}
                  />
                  <span className="field__hint">{t('branding.nameArHint')}</span>
                </label>

                <label className="field">
                  <span className="field__label">{t('branding.nameEn')}</span>
                  <input
                    className="field__input"
                    dir="ltr"
                    lang="en"
                    maxLength={160}
                    value={current.displayNameEn}
                    disabled={!canManage}
                    onChange={(event) => update({ displayNameEn: event.target.value })}
                  />
                  <span className="field__hint">{t('branding.nameEnHint')}</span>
                </label>

                <label className="field">
                  <span className="field__label">{t('branding.logo')}</span>
                  <input
                    className="field__input"
                    dir="ltr"
                    maxLength={400}
                    value={current.logoObjectKey}
                    disabled={!canManage}
                    placeholder="clinics/partner-a/logo.svg"
                    onChange={(event) => update({ logoObjectKey: event.target.value })}
                  />
                  <span className="field__hint">{t('branding.logoHint')}</span>
                </label>

                <label className="field">
                  <span className="field__label">{t('branding.accent')}</span>
                  <div className="toolbar">
                    <input
                      type="color"
                      aria-label={t('branding.accent')}
                      value={normaliseColour(current.accentColour)}
                      disabled={!canManage}
                      onChange={(event) => update({ accentColour: event.target.value.toUpperCase() })}
                    />
                    {/* The wrapping label names the swatch, and two controls cannot share one
                        label — so the hex field states its own name. */}
                    <input
                      className="field__input"
                      aria-label={t('branding.accentHex')}
                      dir="ltr"
                      maxLength={9}
                      value={current.accentColour}
                      disabled={!canManage}
                      onChange={(event) => update({ accentColour: event.target.value })}
                    />
                  </div>
                  <span className="field__hint">{t('branding.accentHint')}</span>
                </label>
              </div>

              <section style={{ marginBlockStart: 'var(--space-5)' }}>
                <h3 className="panel__heading">{t('branding.preview')}</h3>
                <BrandPreview accent={current.accentColour} name={current.displayNameAr || current.displayNameEn} />
                <AccentContrast accent={current.accentColour} />
              </section>

              {save.error ? <ErrorState error={save.error} /> : null}

              {canManage ? (
                <div className="toolbar" style={{ marginBlockStart: 'var(--space-4)' }}>
                  <button type="submit" className="button button--primary" disabled={draft === null || save.isPending}>
                    {save.isPending ? t('catalog.saving') : t('branding.save')}
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={draft === null}
                    onClick={() => setDraft(null)}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              ) : null}
            </form>
          );
        }}
      </AsyncSection>
    </>
  );
}

/**
 * The accent as the patient app will actually use it.
 *
 * Rendered against both the light surface and a dark one, because the app has both and an accent
 * that only works on one of them is an accent that fails half the time.
 */
function BrandPreview({ accent, name }: { readonly accent: string; readonly name: string }) {
  const colour = normaliseColour(accent);
  return (
    <div className="form-grid" data-testid="branding-preview">
      {(['light', 'dark'] as const).map((surface) => (
        <div
          key={surface}
          style={{
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: surface === 'light' ? '#FFFFFF' : '#12151A',
            color: surface === 'light' ? '#12151A' : '#FFFFFF',
            border: '1px solid var(--colour-border)',
          }}
        >
          <p style={{ margin: 0, fontWeight: 600 }}>{name || 'Laseera'}</p>
          <button
            type="button"
            disabled
            style={{
              marginBlockStart: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: colour,
              color: readableTextOn(colour),
              font: 'inherit',
            }}
          >
            {surface === 'light' ? 'احجزي الآن' : 'Book now'}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * What the accent scores, and what that means for the people reading it.
 *
 * Stated as a fact with a number rather than as an error: the clinic owns this decision, and a
 * screen that refused a brand colour would simply be ignored. What it cannot do is stay silent.
 */
function AccentContrast({ accent }: { readonly accent: string }) {
  const { t } = useTranslation();
  const { ratio, level } = accentReadability(normaliseColour(accent));
  const rounded = ratio.toFixed(1);

  if (level === 'good') {
    return (
      <p className="muted" style={{ marginBlockStart: 'var(--space-3)' }}>
        {t('branding.contrastGood', { ratio: rounded })}
      </p>
    );
  }

  return (
    <p
      className={level === 'poor' ? 'callout callout--warning' : 'callout'}
      style={{ marginBlockStart: 'var(--space-3)' }}
    >
      {t(level === 'poor' ? 'branding.contrastPoor' : 'branding.contrastLarge', { ratio: rounded })}
    </p>
  );
}

/** A value the native colour input will accept; it rejects anything that is not `#rrggbb`. */
function normaliseColour(value: string): string {
  return /^#[0-9A-Fa-f]{6}$/.test(value) ? value : DEFAULT_ACCENT;
}

/**
 * The relative luminance of a `#rrggbb` colour, per WCAG.
 *
 * Not a naive average: a mid-green and a mid-blue with the same arithmetic mean are nowhere near
 * equally legible, and this preview exists to tell the truth.
 */
function luminanceOf(hex: string): number {
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

function contrastRatio(a: string, b: string): number {
  const first = luminanceOf(a);
  const second = luminanceOf(b);
  const [lighter, darker] = first > second ? [first, second] : [second, first];
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Black or white text on the accent, whichever a reader can actually see.
 *
 * This used to compare the accent's luminance against a threshold of 0.45 and take white below it.
 * A threshold is a guess at the answer to a question that has an exact one, and it was wrong in a
 * band that matters: Laseera's own orchid sits at 0.23, took white, and produced 3.76:1 — a label a
 * patient in daylight cannot read, previewed to the clinic as though it were fine. Both candidates
 * are now measured and the better one wins, which is also what makes {@link accentReadability}
 * able to say when neither is good enough.
 */
function readableTextOn(hex: string): string {
  return contrastRatio(hex, '#FFFFFF') >= contrastRatio(hex, '#12151A') ? '#FFFFFF' : '#12151A';
}

/**
 * What the clinic's chosen accent will actually do to a patient trying to read a button.
 *
 * A clinic picks its accent for its brand, not for its contrast, and nothing in the product told
 * them when the two disagree — the preview simply drew an unreadable button and left them to
 * notice. AA for a button label is 4.5:1, and 3:1 is the floor at which a large label is still
 * legible; below that the accent needs darkening whatever the brand book says.
 */
function accentReadability(hex: string): { readonly ratio: number; readonly level: 'good' | 'large' | 'poor' } {
  const ratio = contrastRatio(hex, readableTextOn(hex));
  return { ratio, level: ratio >= 4.5 ? 'good' : ratio >= 3 ? 'large' : 'poor' };
}
